import { useState } from "react";
import { Outlet, useMatches } from "@tanstack/react-router";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import numbers from "core/numbers";

import core from "core";
import useMatomo from "core/hooks/useMatomo";
import useNav from "core/hooks/useNav";

import Box from "ui/atom/Box";
import Icon from "ui/atom/Icon";
import Link from "ui/atom/Link";

import AppBar from "ui/base/AppBar";
import Accordion from "ui/base/Accordion";
import Button from "ui/base/Button";
import List from "ui/base/List";

import Logo from "ui/Logo";
import Profile from "ui/Profile";
import QueryProvider from "ui/Query";

import { useLogout } from "features/auth/fabric";
import { useLayout } from "features/layout/fabric";
import { useStart } from "features/start/fabric";

import base from "theme/base";
import dark from "theme/dark";
import light from "theme/light";

import Msal from "theme/ui/Msal";


Layout.Auth = () => {
	const layout = useLayout();
	const start = useStart();
	const nav = useNav();

	const isAuth = nav.location.pathname.includes("auth") && !nav.location.pathname.includes("update");
	const r = nav.location.search["redirect_uri"] || layout.redirect;

	if (start.user && isAuth) return nav.go({ to: r }); // redirect from auth if logged
	return <Layout.Dark />;
};

const Account = ({ sidebar }) => {
	const layout = useLayout();
	const theme = useTheme();
	const nav = useNav();
	const start = useStart();
	const lite = useMediaQuery(t => t.breakpoints.down("md"));

	const loggedIn = Boolean(start.user);

	const user = start.user;
	const profile = start.profile;
	const insufficient = profile?.balance <= 0;
	const name = user?.nickname || user?.name;

	const style = {
		root: { justifyContent: "center", textAlign: "right" },
		balance: { alignItems: "center", justifyContent: "flex-end" }
	};

	if (!loggedIn) return <Button variant="contained" color="tertiary" onClick={() => nav.preserve("/auth")}>Get Started</Button>;
	return lite
		? <Button.Icon icon="menu" size={22} color={theme.palette.text.light} onClick={layout.openSidebar} sx={style.sidebar} />
		: (
			<Profile name={name} onClick={sidebar && layout.openSidebar}>
				<Box.Column gap={0.5} sx={style.root}>
					<Typography variant="body4" color="text.light">{name}</Typography>
					<Box gap={0.5} sx={style.balance}>
						<Icon.Coin size={10} />
						<Typography variant="body4" color={insufficient ? "error.main" : "text.coin"}>{numbers.format(profile?.balance, "number")}</Typography>
					</Box>
				</Box.Column>
			</Profile>
		);
};

const Header = ({ sidebar }) => {
	const theme = useTheme();

	const style = {
		root: {
			width: { xs: "100%", sm: "calc(100% - 32px)" },
			top: { xs: 0, sm: "16px" }, left: { xs: 0, sm: "16px" },
			background: theme.palette.background.header,
			boxShadow: "0 4px 24px 0 rgba(0, 0, 0, 0.25)",
			borderRadius: { xs: 0, sm: "12px" },
			gridArea: "h"
		},
		toolbar: { height: theme.size.header.height.in, borderRadius: "12px" },
		link: { display: "flex" }
	};

	return (
		<AppBar color="secondary" sx={style.root}>
			<AppBar.Toolbar sx={style.toolbar}>
				<Link to="/" sx={style.link}>
					<Logo.Full size={118} />
				</Link>
				<Account sidebar={sidebar} />
			</AppBar.Toolbar>
		</AppBar>
	);
};

const Sidebar = ({ hidden = false }) => {
	const layout = useLayout();
	const logout = useLogout();
	const theme = useTheme();
	const start = useStart();
	const nav = useNav();
	const isTablet = useMediaQuery(t => t.breakpoints.down("md"));

	const name = start.user?.nickname || start.user?.name;

	const permanent = !hidden && !isTablet;
	const pt = theme.size.header.height.out;

	const style = {
		root: {
			gridArea: "s",
			height: { xs: "100%", md: `calc(100% - ${pt}px)` },
			top: { md: `${pt}px` },
			borderRight: 0,
			minWidth: `${theme.size.sidebar.width}px`,
			pt: { xs: 1, lg: 0 },
			background: {
				xs: theme.palette.background.paper,
				md: hidden ? theme.palette.background.paper : "transparent"
			}
		},
		list: { position: "relative", height: "100%", pl: 3 },
		display: { p: theme.spacing(2, 4), gap: theme.spacing(1.75) },
		menu: { mb: 1.25 },
		mark: { position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)" },
		divider: { my: theme.spacing(2) },
		button: { justifyContent: "flex-start" }
	};

	const sections = [
		{ title: "Account", path: "/account", icon: "id-card" },
		{ title: "Profile", path: "/account/profile", icon: "circle-user-round" },
		{ title: "History", path: "/account/history", icon: "clock" },
		{ divider: true },
		{ title: "Logout", icon: "log-out", click: logout.mutate }
	];

	const go = (to) => {
		layout.closeSidebar();
		nav.go({ to });
	};

	return (
		<Drawer
			open={permanent ? true : layout.sidebar}
			anchor={permanent ? "left" : "right"}
			variant={permanent ? "permanent" : "temporary"}
			onClose={layout.closeSidebar}
			slotProps={{ paper: { sx: style.root } }}
		>
			{!permanent && <Profile.Full size={40} name={name} sx={style.display} />}
			<List sx={style.list}>
				<List.Item dense sx={style.menu}>
					<Typography variant="h6">Menu</Typography>
				</List.Item>
				{sections?.map((sec, i) => {
					const active = nav.location.pathname === sec.path;
					if (sec.divider) return <Divider variant="middle" sx={style.divider} key={i} />;
					return (
						<List.Item dense key={i}>
							<Button
								startIcon={<Icon name={sec.icon} size={22} />}
								variant={active ? "contained" : "text"}
								color={active ? "secondary" : "primary"}
								onClick={() => sec.click ? sec.click() : go(sec.path)}
								fullWidth
								sx={{ ...style.button, display: sec.display && sec.display }}
							>
								{sec.title}
							</Button>
						</List.Item>
					);
				})}
				<Logo.Lite size={210} color={theme.palette.logo.lite} sx={style.mark} />
			</List>
		</Drawer>
	);
};

const Body = ({ children, ...props }) => {
	const style = { gridArea: "b" };
	return <Box {...props} sx={{ ...style, ...props.sx }}>{children}</Box>;
};

const Footer = ({ type }) => {
	const [open, setOpen] = useState(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));
	const start = useStart();
	const contact = start.settings?.contact;
	const email = contact.email;
	const social = contact.social;

	const style = {
		root: {
			gridArea: "f",
			alignItems: "center",
			justifyContent: "center",
			width: "100%",
			padding: theme.spacing(2.5, 2),
			background: theme.palette.background.footer,
			mx: "auto"
		},
		main: {
			width: "100%",
			alignItems: "center",
			justifyContent: "space-between",
			padding: {
				xs: theme.spacing(3),
				sm: theme.spacing(6.25, 7.5, 5, 7.5)
			},
			borderRadius: { xs: "16px", sm: "38px" },
			background: "rgba(0, 0, 0, 0.15)",
			boxShadow: "0px 0px 50px 0px rgba(107, 111, 181, 0.24)"
		},
		inner: {
			width: "100%",
			flexDirection: { xs: "column", sm: "row" },
			flexWrap: { xs: "nowrap", sm: "wrap" },
			alignItems: "flex-start",
			justifyContent: "space-between"
		},
		options: {
			flexWrap: { xs: "nowrap", sm: "wrap" },
			flexDirection: { xs: "column", sm: "row" },
			gap: { xs: 0, sm: 4, md: 12 },
			width: { xs: "100%", sm: "auto" },
			mx: { xs: 0, sm: "auto" }
		},
		accordion: {
			backgroundImage: "none",
			boxShadow: "none",
			"&:before": {
				backgroundColor: "none"
			}
		},
		details: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1.5)
		},
		platforms: {
			width: "100%",
			justifyContent: { xs: "center", sm: "flex-start" }
		},
		contact: {
			mt: theme.spacing(1)
		},
		lite: {
			background: theme.palette.primary.paper,
			position: "fixed", bottom: 0, left: 0, right: 0,
			textAlign: "center",
			p: theme.spacing(1)
		}
	};

	const options = [
		{
			title: "Information",
			items: [
				{ label: "Media Partners", to: "/partners" },
				{ label: "Customers", to: `mailto:${email.info}` },
				{ label: "FAQ", to: start.user ? "/faq" : "/#faq" }
			]
		},
		{
			title: "Company",
			items: [
				{ label: "Careers", to: `mailto:${email.careers}` },
				{ label: "Investors", to: `mailto:${email.investors}` },
				{ label: "Company Info", to: `mailto:${email.info}` }
			]
		},
		{
			title: "Help",
			items: [
				{ label: "Privacy", to: "/legal/privacy.html", tab: true },
				{ label: "Legal", to: "/legal/legal.html", tab: true },
				{ label: "Terms", to: "/legal/tou.html", tab: true },
				{ label: "SMS Terms", to: "/legal/smsTerms.html", tab: true }
			]
		}
	];

	const change = (v) => setOpen(s => s === v ? null : v);

	if (type === "redeem") return (
		<Box.Column gap={0.5} sx={style.lite}>
			<Typography variant="body4" color="text.secondary" >
			By redeeming this gift, you acknowledge that you have read and understand the
			</Typography>
			<Link variant="body4" tab to="/legal/privacy.html" color="tertiary.main">Subly Privacy Policy</Link>
		</Box.Column>
	);

	if (type === "lite") return (
		<Typography component="span" variant="body4" color="text.secondary" sx={style.lite}>
			By continuing, you agree to or <Link tab to="/legal/tou.html" color="tertiary.main">Terms & Conditions</Link>.
		</Typography>
	);
	return (
		<Box.Column maxWidth="lg" gap={3.5} sx={style.root}>
			<Box.Column gap={1.25} sx={style.main}>
				<Box gap={2} sx={style.inner}>
					<Logo.Full size={215} />
					<Box sx={style.options}>
						{options.map((o, k) => (
							<Accordion disableGutters expanded={!isMobile || open === k} onChange={() => change(k)} slotProps={{ root: { sx: style.accordion } }} key={k}>
								<Accordion.Summary icon={!isMobile ? false : open === k ? "minus" : "plus" } variant="reverse">
									{o.title}
								</Accordion.Summary>
								<Accordion.Details sx={style.details}>
									{o.items.map((item, i) => <Link tab={item.tab} variant="body2" to={item.to} key={i}>{item.label}</Link>)}
								</Accordion.Details>
							</Accordion>
						))}
					</Box>
				</Box>
				<Box gap={2} sx={style.platforms}>
					<Button.Icon icon="facebook" size={25} tab href={social.facebook || null} />
					<Button.Icon icon="instagram" size={25} tab href={social.instagram || null} />
					<Button.Icon icon="twitter" size={25} tab href={social.x || null} />
					<Button.Icon icon="linkedin" size={25} tab href={social.linkedin || null} />
				</Box>
				<Typography variant="body1">
					<b>Questions?</b> <Link to={`mailto:${email.info}`} color="text.secondary" sx={style.contact}>Contact us</Link>
				</Typography>
			</Box.Column>
			<Typography variant="body4" color="text.secondary">© {new Date().getFullYear()} Subly Media Inc.</Typography>
		</Box.Column>
	);
};

Layout.Light = () => {
	const theme = core.mergeTheme(base, light);;
	const size = theme.size;

	const style = {
		root: {
			display: "grid",
			gridTemplateRows: {
				xs: `${size.header.height.in}px minmax(calc(100vh - ${size.header.height.out}px - ${size.footer.height}px), 1fr) auto`,
				sm: `${size.header.height.out}px minmax(calc(100vh - ${size.header.height.out}px - ${size.footer.height}px), 1fr) auto`
			},
			backgroundColor: "background.default",
			gridTemplateColumns: { md: `${size.sidebar.width}px 1fr` },
			minHeight: "100vh",
			gridTemplateAreas: {
				xs: `
					"h"
					"b"
					"f"
				`,
				md: `
					"h h"
					"s b"
					"s f"
				`
			}
		}
	};

	return (
		<Layout theme={theme}>
			<Box sx={style.root}>
				<Header />
				<Sidebar />
				<Body>
					<Outlet />
				</Body>
			</Box>
		</Layout>
	);
};

Layout.Dark = () => {
	const theme = core.mergeTheme(base, dark);
	const matches = useMatches();

	const match = matches[matches.length - 1];
	const size = theme.size;

	const data = match?.staticData;
	const lite = data?.lite;
	const footer = data?.footer;

	const style = {
		root: {
			display: "grid",
			gridTemplateRows: lite ? "1fr" : `${size.header.height.in}px minmax(calc(100vh - ${size.header.height.out}px - ${size.footer.height}px), 1fr) auto`,
			background: theme.palette.background.paper,
			minHeight: "100vh",
			alignItems: lite ? "center" : undefined,
			gridTemplateColumns: "100%",
			gridTemplateAreas: lite
				? `
					"b"
				`
				: `
					"h"
					"b"
					"f"
				`
		}
	};

	return (
		<Layout theme={theme}>
			<Box sx={style.root}>
				{!lite && (
					<>
						<Header sidebar />
						<Sidebar hidden />
					</>
				)}
				<Body>
					<Outlet />
				</Body>
				{footer && <Footer type={footer} />}
			</Box>
		</Layout>
	);
};

const Content = ({ children }) => (
	<>
		{children}
		<CssBaseline />
		<ReactQueryDevtools buttonPosition="bottom-right" />
	</>
);

export default function Layout({ theme, children }) {
	const hideMsal = import.meta.env.MODE !== "staging";
	useMatomo();

	return (
		<QueryProvider>
			<ThemeProvider theme={theme}>
				{hideMsal
					? <Content>{children}</Content>
					: (
						<Msal>
							<Content>
								{children}
							</Content>
						</Msal>
					)
				}
			</ThemeProvider>
		</QueryProvider>
	);
}